"use client";

import { useEffect, useState } from "react";
import he from 'he';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Paper,
  FormControl,
} from "@mui/material";
import { validateEmail } from "@/utils/validation";
import { removeEmojiHtmlTag } from "@/utils/sanitizeInput";
import { useAlert } from "@/contexts/AlertContext";
import { apiRemote } from "@/api";

// Custom X icon since we can't use lucide-react
const XIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom Paperclip icon
const PaperclipIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface OpenEnrolCourseOrderDialogProps {
  open: boolean;
  onClose: () => void;
  initialData?: any;
  reloadData: () => void;
}

export default function OpenEnrolCourseOrderDialog({ open, onClose, initialData, reloadData }: OpenEnrolCourseOrderDialogProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    file: null as File | null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false)
  
  const alert = useAlert();
  
  const generateOrderCode = (courseId: number) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let randomPart = "";
    for (let i = 0; i < 6; i++) {
      randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `Course${courseId}${randomPart}`;
  };
  
  const [orderCode, setOrderCode] = useState(() => 
    generateOrderCode(initialData?.course_id)
  );

  useEffect(() => {
    if (initialData?.course_id) {
      setOrderCode(generateOrderCode(initialData.course_id));
    }
  }, [initialData?.course_id]);

  useEffect(() => {
    if (initialData?.user?.display_name) {
      const fullName = he.decode(initialData.user.display_name).trim();
      const nameParts = fullName.split(" ");
  
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      const email = initialData?.user?.user_email || "";
  
      setFormData((prev) => ({
        ...prev,
        firstName,
        lastName,
        email,
      }));
    }
  }, [initialData]);

  const resetFormData = () => {
    setFormData({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      file: null,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log(initialData);
    if (!formData.file) {
        alert.error("Please attach a file.");
        return;
    }

    if (Object.values(errors).some((error) => error)) {
      alert.error("Please fix the errors before submitting.");
      return;
    }

    setIsSubmitting(true);
    // console.log(formData);
    apiRemote.sendRequestCourse({
        billing_first_name: formData.firstName,
        billing_last_name: formData.lastName,
        billing_phone: formData.phone,
        billing_email: formData.email,
        course_id: initialData?.course_id,
        course_name: initialData?.course_name,
        course_cost: initialData?.cost,
        course_currency: initialData?.currency,
        proofOfPayment: formData.file,
        order_code: orderCode,
        for_approval: initialData?.for_approval,
    }).then((res)=>{
        if(res.code == 200 || res.code == 201){
            alert.success("Request sent successfully");
            handleClose();
            reloadData();
        }else{
            alert.error(res.message);
        }
        setIsSubmitting(false)
        
    }).catch((err)=>{
        alert.error(err.message);
        setIsSubmitting(false)
        throw err;
    });
  };

  const transferInfo = {
    nameOfAccount: process.env.NAME_OF_ACCOUNT,
    bankOfAccount: process.env.BANK_OF_ACCOUNT,
    bankAccountNo: process.env.NUMBER_OF_ACCOUNT,
    bankAddress: process.env.BANK_ADDRESS,
    amount: initialData?.cost + ' ' + initialData?.currency,
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    let sanitizedValue = value;

    if (name === "email") {
      const emailError = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: removeEmojiHtmlTag(sanitizedValue),
    }));
  };

  const handleInputFileChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    let sanitizedValue: string | File | null = value;

    if (name === "file") {
      const inputElement = e.target as HTMLInputElement;
      const file1 = inputElement.files ? inputElement.files[0] : null;
      sanitizedValue = file1; // Gán file1 vào sanitizedValue
    }
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  };
  const handleClose = () => {
    resetFormData();
    onClose();
  };

  const summaryStyles = `
    .course-summary-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0;
    }
    .course-summary-content td {
      border: 1px dashed #bbb;
      position: relative;
      min-width: 2rem;
      height: 2rem;
      padding: 8px;
      font-size: 14px;
    }
    .course-summary-content th {
      border: 1px solid #ddd;
      padding: 8px;
      background-color: #f5f5f5;
      font-weight: bold;
    }
    .course-summary-content img {
      width: 500px;
      height: 300px;
    }
    .course-summary-content tr:nth-child(even) {
      background-color: #f9f9f9;
    }
  `

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <style>{summaryStyles}</style>
      <DialogTitle
        style={{
          margin: 0,
          padding: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Course Order
        <IconButton
          onClick={handleClose}
          style={{
            color: "#666",
          }}
        >
          <XIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: 24 }}>
        <form onSubmit={handleSubmit}>
          {initialData?.course?.summary && initialData?.course?.summary != '' && (
          <>
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              Course Summary
            </Typography>
            <Paper
              variant="outlined"
              style={{
                padding: 16,
                marginBottom: 24,
              }}
            >
              <>
                <Box
                  className="course-summary-content"
                  dangerouslySetInnerHTML={{ __html: initialData.course.summary }}
                  style={{
                    fontSize: "14px",
                    color: "#333",
                    maxHeight: summaryExpanded ? "none" : "100px",
                    overflow: summaryExpanded ? "visible" : "hidden",
                    position: "relative",
                  }}
                  sx={{
                    "&::after": !summaryExpanded
                      ? {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "40px",
                        background: "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
                      }
                      : {},
                  }}
                />
                <Button
                  onClick={() => setSummaryExpanded(!summaryExpanded)}
                  style={{
                    marginTop: 8,
                    textTransform: "none",
                    padding: "4px 8px",
                    minWidth: "auto",
                  }}
                >
                  {summaryExpanded ? "Show less" : "Show more"}
                </Button>
              </>
            </Paper>
          </>
          )}
          <Box style={{ marginBottom: 24 }}>
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              Payment Information
            </Typography>
            <Box
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <FormControl fullWidth margin="normal">
                <Typography variant="body1" color="textSecondary">
                  First name:{" "}
                  <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl fullWidth margin="normal">
                <Typography variant="body1" color="textSecondary">
                  Last name:{" "}
                  <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>
            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Phone:{" "}
              </Typography>
              <TextField
                fullWidth
                name="phone"
                style={{ marginBottom: 16 }}
                value={formData.phone}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Email address:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <TextField
                required
                fullWidth
                type="email"
                name="email"
                value={formData.email}
                error={!!errors.email}
                helperText={errors.email}
                onChange={handleChange}
              />
            </FormControl>
          </Box>

          <Paper
            variant="outlined"
            style={{
              padding: 16,
              marginBottom: 24,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              THE TRANSFER INFORMATION
            </Typography>
            <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Typography variant="body2">
                - Order Code:{" "}
                <Typography component="span" color="error" fontWeight="bold">
                  {orderCode}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Name of account:{" "}
                <Typography component="span" color="error">
                  {transferInfo.nameOfAccount}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Bank of account:{" "}
                <Typography component="span" color="error">
                  {transferInfo.bankOfAccount}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Number of account:{" "}
                <Typography component="span" color="error">
                  {transferInfo.bankAccountNo}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Bank branch:{" "}
                <Typography component="span" color="error">
                  {transferInfo.bankAddress}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Amount payable:{" "}
                <Typography component="span" color="error">
                  {transferInfo.amount}
                </Typography>
              </Typography>
            </Box>
          </Paper>
          <Box
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 24,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" style={{ textAlign: "center", color: "#333" }}>
              Complete your payment and click <strong>'Attach'</strong> to upload necessary
              documents, like proof of payment.
            </Typography>

            <label
              htmlFor="file-upload"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: 4,
                cursor: "pointer",
                backgroundColor: "#f5f5f5",
              }}
            >
              <PaperclipIcon />
              <Typography style={{ textTransform: "none" }}>Attach</Typography>
              <input
                id="file-upload"
                style={{ display: "none" }}
                type="file"
                name="file"
                accept=".jpeg,.jpg,.png"
                onChange={handleInputFileChange}
              />
            </label>

            {formData.file && (
              <Typography variant="body2" style={{ color: "#555" }}>
                Attached file: <strong>{formData.file.name}</strong>
              </Typography>
            )}
          </Box>


          <Button
            type="submit"
            variant="contained"
            fullWidth
            style={{
              backgroundColor: "rgb(15, 105, 185)",
              textTransform: "none",
            }}
            disabled={isSubmitting}
          >
            Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
